import React, { useState, useRef, useEffect, useCallback } from 'react';

const clamp = (v, min = 2, max = 98) => Math.min(Math.max(v, min), max);

export default function BeforeAfterSlider({ beforeSrc, afterSrc, beforeAlt = 'Vorher', afterAlt = 'Nachher' }) {
  const [pos, setPos] = useState(50);
  const [dragging, setDragging] = useState(false);
  const containerRef = useRef(null);
  const animRef = useRef(null);
  const hinted = useRef(false);

  const toPercent = useCallback((clientX) => {
    const r = containerRef.current?.getBoundingClientRect();
    if (!r) return 50;
    return clamp(((clientX - r.left) / r.width) * 100);
  }, []);

  // Hint animation: 50 → 30 → 50 over 1500ms using sine curve
  const runHint = useCallback(() => {
    if (hinted.current) return;
    hinted.current = true;
    let t0 = null;
    const D = 1500;
    const frame = (ts) => {
      if (!t0) t0 = ts;
      const p = Math.min((ts - t0) / D, 1);
      const bell = Math.sin(Math.PI * p); // 0 → 1 → 0
      setPos(50 - bell * 20); // 50 → 30 → 50
      if (p < 1) animRef.current = requestAnimationFrame(frame);
      else setPos(50);
    };
    animRef.current = requestAnimationFrame(frame);
  }, []);

  // Trigger hint when slider enters viewport
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setTimeout(runHint, 500); io.disconnect(); }
    }, { threshold: 0.4 });
    io.observe(el);
    return () => { io.disconnect(); cancelAnimationFrame(animRef.current); };
  }, [runHint]);

  // Global mouse move/up for drag
  useEffect(() => {
    const move = (e) => { if (dragging) setPos(toPercent(e.clientX)); };
    const up = () => setDragging(false);
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, [dragging, toPercent]);

  return (
    <div
      ref={containerRef}
      className="relative select-none overflow-hidden rounded-xl"
      style={{
        aspectRatio: '4/3',
        touchAction: 'none',
        cursor: 'ew-resize',
        boxShadow: '0 8px 40px rgba(26,26,46,0.16)',
      }}
      onTouchMove={(e) => { e.preventDefault(); setPos(toPercent(e.touches[0].clientX)); }}
    >
      {/* Nachher (full background) */}
      <img
        src={afterSrc}
        alt={afterAlt}
        className="absolute inset-0 w-full h-full object-cover"
        draggable={false}
      />

      {/* Vorher (clipped to left of divider) */}
      <div
        className="absolute inset-0"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      >
        <img
          src={beforeSrc}
          alt={beforeAlt}
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
        />
      </div>

      {/* Labels */}
      <span className="absolute top-3 left-4 z-10 bg-[#1A1A2E]/80 text-white text-xs font-bold px-2.5 py-1 rounded pointer-events-none">
        Vorher
      </span>
      <span className="absolute top-3 right-4 z-10 bg-white/90 text-[#1A1A2E] text-xs font-bold px-2.5 py-1 rounded pointer-events-none">
        Nachher
      </span>

      {/* Divider line */}
      <div
        className="absolute top-0 bottom-0 z-20"
        style={{ left: `${pos}%`, transform: 'translateX(-50%)', width: '2px', backgroundColor: '#E8722A' }}
      >
        {/* Handle */}
        <div
          className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-[#E8722A] border-[3px] border-white shadow-xl flex items-center justify-center"
          style={{ cursor: 'ew-resize' }}
          onMouseDown={(e) => { e.preventDefault(); setDragging(true); }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') setPos((p) => clamp(p - 5));
            if (e.key === 'ArrowRight') setPos((p) => clamp(p + 5));
          }}
          tabIndex={0}
          role="slider"
          aria-label="Vorher/Nachher Schieberegler"
          aria-valuenow={Math.round(pos)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <svg width="14" height="10" viewBox="0 0 14 10" fill="none" aria-hidden="true">
            <path d="M4 1L1 5L4 9M10 1L13 5L10 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
      </div>
    </div>
  );
}
